/**
 * CreditWarningController
 *
 * Global component that listens to EventEmitter for credit-related events
 * and renders the appropriate warning modal. Mounted once in Container.
 * It also acts as a bridge for async credit updates from utils to Redux.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import EventEmitter from "../utils/emitter/EventEmitter";
import {
  CREDIT_MODAL_TYPES,
  STATUS_CODE_TO_MODAL,
  ALL_CREDIT_STATUS_CODES,
  EMITTER_CREDIT_WARNING,
  EMITTER_LOW_CREDITS,
  EMITTER_CREDIT_UPDATE,
  EMITTER_SDR_STATUS,
} from "../utils/creditMonitor/creditStatusCodes";
import {
  UPDATE_AVAILABLE_CREDITS,
  UPDATE_IS_SDR,
} from "../redux/constants/authConstants";

// Modal components
import InsufficientCreditsModal from "./Modals/CreditWarningModals/InsufficientCreditsModal";
import FeatureNotInPlanModal from "./Modals/CreditWarningModals/FeatureNotInPlanModal";
import MissingCreditRecordModal from "./Modals/CreditWarningModals/MissingCreditRecordModal";
import ZeroCreditsModal from "./Modals/CreditWarningModals/ZeroCreditsModal";
import LowCreditsWarningModal from "./Modals/CreditWarningModals/LowCreditsWarningModal";

const CreditWarningController = () => {
  const dispatch = useDispatch();
  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [availableCredits, setAvailableCredits] = useState(null);

  // Check if user is on SDR plan - suppress all credit warnings
  const userDetails = useSelector((state) => state?.userDetails?.payload);
  const userIsSdr =
    userDetails?.isSdr === true || userDetails?.isSdr === "true";

  // Debounce ref to prevent multiple low-credit modals in quick succession
  const lowCreditDebounceRef = useRef(null);

  const handleCreditWarning = useCallback((data) => {
    // Fix R6: derive modalType from statusCode when an emitter sent only a statusCode (e.g. the AI
    // Sequence Builder credit gate emits { statusCode } with no modalType) — else the event is dropped
    // and no modal ever renders.
    const modalType = data?.modalType || STATUS_CODE_TO_MODAL[data?.statusCode];
    if (!modalType) return;
    // Fix R7: for SDR users suppress only genuine CREDIT warnings (codes 301-304), not the
    // non-credit / system events that also travel on this channel.
    if (userIsSdr && ALL_CREDIT_STATUS_CODES.includes(data?.statusCode)) return;
    setActiveModal(modalType);
    setModalMessage(data.message || "");
  }, [userIsSdr]);

  const handleLowCredits = useCallback((data) => {
    // Never show low credit warnings for SDR plans
    if (userIsSdr) return;
    // Debounce to avoid showing modal on every API call when credits are low
    if (lowCreditDebounceRef.current) return;

    setActiveModal(CREDIT_MODAL_TYPES.LOW_CREDITS);
    setModalMessage(data?.message || "");
    setAvailableCredits(data?.availableCredits ?? null);

    // Set debounce for 60 seconds - don't show low credit modal again within 60s
    lowCreditDebounceRef.current = setTimeout(() => {
      lowCreditDebounceRef.current = null;
    }, 60000);
  }, [userIsSdr]);

  const handleCreditUpdate = useCallback(
    (credits) => {
      if (typeof credits === "number") {
        dispatch({
          type: UPDATE_AVAILABLE_CREDITS,
          payload: credits,
        });
      }
    },
    [dispatch],
  );

  const handleSdrStatus = useCallback(
    (isSdr) => {
      dispatch({
        type: UPDATE_IS_SDR,
        payload: !!isSdr,
      });
    },
    [dispatch],
  );

  useEffect(() => {
    EventEmitter.on(EMITTER_CREDIT_WARNING, handleCreditWarning);
    EventEmitter.on(EMITTER_LOW_CREDITS, handleLowCredits);
    EventEmitter.on(EMITTER_CREDIT_UPDATE, handleCreditUpdate);
    EventEmitter.on(EMITTER_SDR_STATUS, handleSdrStatus);

    return () => {
      EventEmitter.removeEventListener(
        EMITTER_CREDIT_WARNING,
        handleCreditWarning,
      );
      EventEmitter.removeEventListener(EMITTER_LOW_CREDITS, handleLowCredits);
      EventEmitter.removeEventListener(
        EMITTER_CREDIT_UPDATE,
        handleCreditUpdate,
      );
      EventEmitter.removeEventListener(EMITTER_SDR_STATUS, handleSdrStatus);
      if (lowCreditDebounceRef.current) {
        clearTimeout(lowCreditDebounceRef.current);
      }
    };
  }, [handleCreditWarning, handleLowCredits, handleCreditUpdate, handleSdrStatus]);

  const handleClose = (val) => {
    setActiveModal(null);
    setModalMessage("");
    setAvailableCredits(null);
  };

  const isOpen = activeModal !== null;

  // Render the appropriate modal based on active type
  switch (activeModal) {
    case CREDIT_MODAL_TYPES.INSUFFICIENT:
      return (
        <InsufficientCreditsModal
          open={isOpen}
          close={handleClose}
          message={modalMessage}
        />
      );

    case CREDIT_MODAL_TYPES.NOT_IN_PLAN:
      return (
        <FeatureNotInPlanModal
          open={isOpen}
          close={handleClose}
          message={modalMessage}
        />
      );

    case CREDIT_MODAL_TYPES.MISSING_RECORD:
      return (
        <MissingCreditRecordModal
          open={isOpen}
          close={handleClose}
          message={modalMessage}
        />
      );

    case CREDIT_MODAL_TYPES.ZERO_CREDITS:
      return (
        <ZeroCreditsModal
          open={isOpen}
          close={handleClose}
          message={modalMessage}
        />
      );

    case CREDIT_MODAL_TYPES.LOW_CREDITS:
      return (
        <LowCreditsWarningModal
          open={isOpen}
          close={handleClose}
          message={modalMessage}
          availableCredits={availableCredits}
        />
      );

    default:
      return null;
  }
};

export default CreditWarningController;
